import React, { useEffect, useRef, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import ActionButton from "../ActionButton";

// Two-press guard grace window - see the handlePress JSDoc below.
// Module scope so it is not re-declared on every render.
const ARM_TO_CONFIRM_GRACE_MS = 500;

// How long an armed guard stays armed - see the ARMED EXPIRY comment
// on its effect below for why this exists and how the number was
// chosen.
const ARMED_EXPIRY_MS = 20_000;

/**
 * Start Over, with the arm-to-confirm guard every destructive reset in
 * this tool now shares.
 *
 * WHY IT EXISTS : Start Over discarded the issuance request on the
 * server the instant it was pressed, on every step, with no warning.
 * A customer who had not saved a configuration file lost the order,
 * the keys and the validation work in one click - and the button sits
 * beside the primary action in the same action row on every step, so
 * it is an easy mis-press. The operator's words on watching it happen : "Do
 * you not think everyone will click it! Including me". Confirmation
 * ordered 2026-08-17.
 *
 * WHY THIS SHAPE : the tool already confirmed this exact action in one
 * place - Step 4's issued screen swapped its button label to "Press
 * Again to Confirm" rather than opening a box - and that pattern had
 * already shipped and been reviewed. Copying it rather than inventing
 * a second confirmation idiom is the operator's standing site-canon
 * rule (2026-08-15) and his ruling on this change (2026-08-17). The
 * guard's state, grace window and label swap moved here out of
 * Step4Download so every Start Over in the tool shares them and none
 * can drift apart.
 *
 * The confirm label names the CONSEQUENCE rather than asking "are you
 * sure", and it has to do that in words that fit the button. These sit
 * in a two-column grid beside Continue, so roughly half of a
 * max-w-2xl row : the first wording here ("Press Again to Confirm -
 * This Discards the Request") ran to two lines the moment it armed,
 * which is the button-text bulk the operator has objected to twice.
 * Keep any new label to about four words.
 *
 * @param {function} onReset - runs on the CONFIRMED second press
 * @param {string} [label] - resting label
 * @param {string} [confirmLabel] - armed label, names what is lost
 * @param {string} [armedAnnouncement] - what the sr-only live region
 *   says once armed. A full sentence rather than the button's own
 *   label : the label is written to fit a narrow button, which is not
 *   a constraint speech has.
 * @param {boolean} [disabled] - greys it out, blocks the press, and
 *   stands the guard down if it was armed
 * @param {string} [variant] - ActionButton variant
 * @param {string} [type] - button type ; "button" inside a <form>
 * @param {*} [disarmToken] - change this value to disarm from outside
 */
function StartOverButton({
  onReset,
  label = "Start Over",
  confirmLabel = "Press Again to Discard",
  armedAnnouncement = "Start Over again to discard this issuance request. It cannot be recovered without a configuration file you have already saved.",
  disabled = false,
  variant = "outline",
  type,
  disarmToken,
}) {
  const [armed, setArmed] = useState(false);
  const armedAtRef = useRef(0);

  // Outside disarm (2026-08-14 go-public review, carried over from
  // Step4Download) : a customer who arms this and then thinks better
  // of it and downloads their files instead must not be left with a
  // primed button, or their next press is instantly destructive. The
  // caller bumps disarmToken from its own download handlers. The token
  // is compared with the value seen on the previous render, DURING the
  // render, rather than in an effect : the disarm then lands in the same
  // commit as the token change instead of one render later, and there is
  // no mount case to skip because the first render has no previous value
  // to differ from (react-hooks/set-state-in-effect ; the shape is React's
  // own "storing information from previous renders" pattern).
  const [seenDisarmToken, setSeenDisarmToken] = useState(disarmToken);
  if (disarmToken !== seenDisarmToken) {
    setSeenDisarmToken(disarmToken);
    setArmed(false);
  }

  // Going DISABLED disarms too, and for the same changed-intent
  // reason. Step 2 is the case : its Start Over greys out while the
  // issuance submit is in flight, so a customer who armed this and
  // then pressed Continue has plainly chosen to carry on - yet the
  // flag would survive the round trip and leave the re-enabled button
  // one press from discarding the request they just created. A
  // disabled button also cannot be confirmed, so by the time it comes
  // back the arming is stale whatever the reason for it. Same
  // previous-render comparison as the token above, for the same reason.
  const [seenDisabled, setSeenDisabled] = useState(disabled);
  if (disabled !== seenDisabled) {
    setSeenDisabled(disabled);
    if (disabled) {
      setArmed(false);
    }
  }

  // ARMED EXPIRY : the guard stands itself down after a while, and
  // this is what actually CLOSES the changed-intent family rather
  // than chasing it (2026-08-17 review round 3).
  //
  // The disarm token above only fires from controls somebody
  // remembered to wire. Three review rounds each found another
  // control that had been missed - Step 3's step-away save, then Step
  // 4's, then the seven inside ChallengePanel (the HTTP-01 file
  // download, four Copy controls, Request Validation and Check
  // Validation Status), none of which can even reach Step 3's token
  // because the panel is not passed it. Enumerating harder was
  // finding one more sibling per round ; an expiry covers every
  // member that exists, every member nobody listed, and every control
  // added later, by construction.
  //
  // The failure mode is deliberately the safe one : a customer who
  // reads slowly and presses late has merely re-armed, which costs
  // one extra click and destroys nothing. Nothing is lost by
  // expiring ; something can be lost by staying armed.
  //
  // TWENTY seconds, not the ten first suggested : the sr-only warning
  // below is a full two-sentence announcement, which takes a screen
  // reader something like eight seconds to speak, and a blind
  // customer cannot begin deciding until it finishes. Ten would have
  // expired the guard while it was still being read out - on exactly
  // the customer this region was added for. Twenty leaves room for
  // the announcement plus deliberation, and still expires long before
  // any real excursion (fetching a challenge file, uploading it to a
  // web server, coming back) could complete.
  useEffect(() => {
    if (!armed) {
      return undefined;
    }
    const id = setTimeout(() => setArmed(false), ARMED_EXPIRY_MS);
    return () => clearTimeout(id);
  }, [armed]);

  /**
   * ARM-TO-CONFIRM GRACE : a physical double-click delivers two
   * discrete click events on this same button and React flushes the
   * arming state between them, so without the grace window the second
   * half of a double-click WAS the confirmation - the guard destroyed
   * the work on exactly the accidental input it exists to absorb. A
   * confirm press landing within the grace of arming is ignored.
   * ARM_TO_CONFIRM_GRACE_MS sits far above any double-click interval
   * and far below a deliberate read-the-new-label-then-press
   * confirmation.
   */
  const handlePress = () => {
    if (!armed) {
      setArmed(true);
      armedAtRef.current = Date.now();
      return;
    }
    if (Date.now() - armedAtRef.current < ARM_TO_CONFIRM_GRACE_MS) {
      return;
    }
    // Stand down BEFORE handing over. Step 1 is the case that proves
    // this is not housekeeping : a reset there does NOT unmount this
    // button (Step 1 stays the active step afterwards), so an armed
    // flag left set would leave the customer looking at a freshly
    // reset page whose button still read the confirmation wording -
    // and one click from discarding whatever they started next.
    setArmed(false);
    onReset();
  };

  return (
    <>
      <ActionButton
        type={type}
        onClick={handlePress}
        variant={variant}
        disabled={disabled}
        icon={ArrowPathIcon}
      >
        {armed ? confirmLabel : label}
      </ActionButton>
      {/*
        The armed state, spoken (2026-08-17 review round 1). The ENTIRE
        signal of this guard is the button's accessible name changing
        while focus stays on that same button, and screen readers do
        not reliably re-announce a renamed control that already has
        focus. Without this a blind customer presses Start Over, hears
        nothing, presses again, and the issuance request is gone
        without the warning ever having been delivered - the one
        customer the guard protects least is the one who cannot see
        the label it works by.

        Persistent rather than conditionally inserted, matching the
        per-domain region in ChallengePanel : a live region mounted at
        the moment it gains content is unreliable across screen
        readers, whereas one that is always present and changes text
        announces every time. `polite` because the customer just acted
        deliberately and nothing is failing.
      */}
      <div role="status" aria-live="polite" className="sr-only">
        {armed ? armedAnnouncement : ""}
      </div>
    </>
  );
}

export default StartOverButton;
